// client/src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import SessionTimeout from './components/auth/SessionTimeout';
import './i18n/i18n';
import './styles/Mobile.css';

// Layout
import Layout from './components/layout/Layout';

// Auth pages
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import RegisterAdmin from './components/admin/RegisterAdmin';
import RegisterWithInvite from './components/auth/RegisterWithInvite'; // NEW
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import BankReferralRegistration from './components/auth/BankReferralRegistration';
import Assessment from './components/assessment/Assessment';
import BankManagement from './components/company/BankManagement';

// Admin components
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import PendingUserManagement from './components/admin/PendingUserManagement';
import OrganizationManagement from './components/admin/OrganizationManagement';
import RequestsManagement from './components/admin/RequestsManagement';
import BidsManagement from './components/admin/BidsManagement';
import TransactionsManagement from './components/admin/TransactionsManagement';
import AdminProfile from './components/admin/AdminProfile';
import AdminNotifications from './components/admin/AdminNotifications';
//import AdminKycView from './components/admin/AdminKycView';

// New Admin Detail Components
import AdminUserDetails from './components/admin/AdminUserDetails';
import AdminOrganizationDetails from './components/admin/AdminOrganizationDetails';
import AdminRequestDetails from './components/admin/AdminRequestDetails';
import AdminBidDetails from './components/admin/AdminBidDetails';
import MarketsManagement from './components/admin/MarketsManagement'; // NEW

// Company components
import CompanyDashboard from './components/company/CompanyDashboard';
import NewRequest from './components/company/NewRequest';
import PastRequests from './components/company/PastRequests';
import RequestDetail from './components/company/RequestDetail';
import CompanyProfile from './components/company/CompanyProfile';
import CompanyAnalytics from './components/company/CompanyAnalytics'; 
import Markets from './components/company/Markets'; // NEW
import Performance from './components/company/Performance'; // NEW
//import OrganizationProfile from './components/company/OrganizationProfile'; // ADDED ONLY THIS
import CompanyNotifications from './components/company/CompanyNotifications';
import BankInfo from './components/company/BankInfo'; 
import ContactBank from './components/company/ContactBank';

// Bank components
import BankDashboard from './components/bank/BankDashboard';
import OpenRequests from './components/bank/OpenRequests';
import SubmitBid from './components/bank/SubmitBid';
import BidManagement from './components/bank/BidManagement';
import PastBids from './components/bank/PastBids';
import BidDetail from './components/bank/BidDetail';
import BankProfile from './components/bank/BankProfile';
import BankNotifications from './components/bank/BankNotifications';

// Team Management Component - NEW
import TeamManagement from './components/team/TeamManagement';

// Error and utility components
import NotFound from './components/utils/NotFound';
import Loader from './components/utils/Loader';

// CSS
import './App.css';
import './styles/TeamManagement.css'; // NEW

// Protected route component
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
  return <Loader />; // Aspetta che user sia caricato
  }
  if (roles && !roles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user.role === 'bank') {
      return <Navigate to="/bank/dashboard" replace />;
    } else {
      return <Navigate to="/company/dashboard" replace />;
    }
  }

  return (
    <>
      {/* Aggiungiamo il componente SessionTimeout alle rotte protette */}
      <SessionTimeout timeoutMinutes={15} warningMinutes={1} />
      {children}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-admin" element={<RegisterAdmin />} />
          <Route path="/register/invite/:token" element={<RegisterWithInvite />} /> {/* NEW */}
          <Route path="/bank-referral/register/:token" element={<BankReferralRegistration />} /> {/* NUOVO */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/assessment" element={<Assessment />} />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="pending-users" element={<PendingUserManagement />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="users/:id" element={<AdminUserDetails />} />
            <Route path="organizations" element={<OrganizationManagement />} />
            <Route path="organizations/:id" element={<AdminOrganizationDetails />} />
            {/*<Route path="organizations/:id/kyc" element={<AdminKycView />} />*/}
            <Route path="requests" element={<RequestsManagement />} />
            <Route path="requests/:id" element={<AdminRequestDetails />} />
            <Route path="bids" element={<BidsManagement />} />
            <Route path="bids/:id" element={<AdminBidDetails />} />
            <Route path="transactions" element={<TransactionsManagement />} />
            <Route path="markets" element={<MarketsManagement />} /> {/* NEW */}
            <Route path="profile" element={<AdminProfile />} />
            <Route path="notifications" element={<AdminNotifications />} />
          </Route>

          {/* Company routes */}
          <Route
            path="/company"
            element={
              <ProtectedRoute roles={['company']}>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<CompanyDashboard />} />
            <Route path="new-request" element={<Navigate to="/company/new-request/allocation" replace />} />
            <Route path="new-request/:type" element={<NewRequest />} />
            <Route path="past-requests" element={<Navigate to="/company/past-requests/allocation" replace />} />
            <Route path="past-requests/:type" element={<PastRequests />} />
            <Route path="requests/:id" element={<RequestDetail />} />
            <Route path="bank-info/:bankName" element={<BankInfo />} />
            <Route path="team" element={<TeamManagement />} />
            <Route path="bank-management" element={<BankManagement />} /> 
            <Route path="analytics" element={<CompanyAnalytics />} /> {/* NEW */}
            <Route path="markets" element={<Markets />} /> {/* NEW */}
            <Route path="performance" element={<Performance />} /> {/* NEW */}
            <Route path="profile" element={<CompanyProfile />} />
            {/*<Route path="organization" element={<OrganizationProfile />} />  ADDED ONLY THIS LINE */}
            <Route path="notifications" element={<CompanyNotifications />} />
            <Route path="contact-bank" element={<ContactBank />} />
          </Route>

          {/* Bank routes */}
          <Route
            path="/bank"
            element={
              <ProtectedRoute roles={['bank']}>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<BankDashboard />} />
            <Route path="open-requests" element={<OpenRequests />} />
            <Route path="submit-bid/:id" element={<SubmitBid />} />
            <Route path="my-bids" element={<BidManagement />} />
            <Route path="past-bids" element={<PastBids />} />
            <Route path="bids/:id" element={<BidDetail />} />
            <Route path="team" element={<TeamManagement />} /> {/* NEW */}
            <Route path="profile" element={<BankProfile />} />
            <Route path="notifications" element={<BankNotifications />} />
          </Route>

          {/* Default routes */}
          <Route
            path="/"
            element={
              <Navigate
                to={
                  localStorage.getItem('token')
                    ? '/login'
                    : '/login'
                }
                replace
              />
            }
          />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;